import { Button } from "../../../Button/Button.js";
import { Tooltip } from "../../../Tooltip/Tooltip.js";
import { TextControl } from "../Text/Text.js";
import EditInPlace_module_default from "./EditInPlace.module.js";
import { Label as Label$1 } from "../../Label.js";
import { HelpMessage, LoadingMessage, SuccessMessage } from "../../Message.js";
import { Root as Root$1 } from "../../Root.js";
import { Field as Field$1 } from "../../Field.js";
import { forwardRef, useCallback, useEffect, useReducer, useRef, useState } from "react";
import CloseIcon from "@vector-im/compound-design-tokens/assets/web/icons/close";
import { jsx, jsxs } from "react/jsx-runtime";
import { Submit, ValidityState } from "@radix-ui/react-form";
import Check from "@vector-im/compound-design-tokens/assets/web/icons/check";
//#region src/components/Form/Controls/EditInPlace/EditInPlace.tsx
function reducer(state, action) {
	switch (action) {
		case 0: if (state === 0 || state === 3) return 1;
		else return state;
		case 1: return 2;
		case 4: return 0;
		case 2: if (state === 2) return 3;
		else return state;
		case 3: if (state === 2) return 0;
		else return state;
		case 5: if (state === 3) return 0;
		else return state;
	}
	assertNever(action);
}
function assertNever(value) {
	throw new Error(`Unreachable value: ${value}`);
}
/**
* A text box with save/cancel buttons that appear when the field is active.
* Since thios control has its own 'save' button, it should *not* appear as part
* of a larger form: it exists as its own form that submits separately.
*/
var EditInPlace = forwardRef(function EditInPlace({ className, label, onSave, onCancel, onInput, onClearServerErrors, serverInvalid, saveButtonLabel, cancelButtonLabel, savedLabel, savingLabel, helpLabel, disabled, children, ...props }, ref) {
	const [state, dispatch] = useReducer(reducer, 0);
	const isFocusWithinRef = useRef(false);
	const [isFocusWithin, setFocusWithin] = useState(false);
	const shouldShowSaveButton = state === 1 || state === 2 || isFocusWithin;
	const hideTimer = useRef(void 0);
	useEffect(() => {
		if (state === 3) hideTimer.current = setTimeout(() => {
			dispatch(5);
			hideTimer.current = void 0;
		}, 2e3);
		return () => {
			if (hideTimer.current) clearTimeout(hideTimer.current);
			hideTimer.current = void 0;
		};
	}, [state]);
	const formRef = useRef(null);
	const saveButtonRef = useRef(null);
	const cancelButtonRef = useRef(null);
	const onFocus = useCallback(() => {
		if (isFocusWithinRef.current) return;
		isFocusWithinRef.current = true;
		setFocusWithin(true);
	}, [isFocusWithin, setFocusWithin]);
	const onBlur = useCallback((e) => {
		if (!isFocusWithinRef.current) return;
		if (e.currentTarget.contains(e.relatedTarget)) return;
		isFocusWithinRef.current = false;
		setFocusWithin(false);
	}, [isFocusWithin, setFocusWithin]);
	const onInputHandler = useCallback((e) => {
		dispatch(0);
		onInput?.(e);
	}, [dispatch, onInput]);
	return /* @__PURE__ */ jsx(Root$1, {
		className,
		onSubmit: useCallback(async (e) => {
			e.preventDefault();
			if (state === 0) return;
			try {
				dispatch(1);
				saveButtonRef.current?.blur();
				await onSave?.(e);
				dispatch(2);
			} catch {
				dispatch(3);
			}
		}, [
			onSave,
			state,
			hideTimer
		]),
		onReset: useCallback((e) => {
			cancelButtonRef.current?.blur();
			onCancel?.(e);
			dispatch(4);
		}, [cancelButtonRef, onCancel]),
		onFocus,
		onBlur,
		onClearServerErrors,
		ref: formRef,
		children: /* @__PURE__ */ jsxs(Field$1, {
			name: "input",
			serverInvalid,
			children: [
				/* @__PURE__ */ jsx(Label$1, { children: label }),
				/* @__PURE__ */ jsxs("div", {
					className: EditInPlace_module_default.controls,
					children: [/* @__PURE__ */ jsx(TextControl, {
						ref,
						...props,
						onInput: onInputHandler,
						disabled: disabled || state === 2
					}), shouldShowSaveButton && /* @__PURE__ */ jsxs("div", {
						className: EditInPlace_module_default["button-group"],
						children: [/* @__PURE__ */ jsx(Tooltip, {
							label: saveButtonLabel,
							children: /* @__PURE__ */ jsx(Submit, {
								asChild: true,
								children: /* @__PURE__ */ jsx(Button, {
									type: "submit",
									kind: "primary",
									size: "md",
									ref: saveButtonRef,
									disabled: state !== 1,
									iconOnly: true,
									Icon: Check
								})
							})
						}), /* @__PURE__ */ jsx(Tooltip, {
							label: cancelButtonLabel,
							children: /* @__PURE__ */ jsx(Button, {
								type: "reset",
								kind: "secondary",
								size: "md",
								ref: cancelButtonRef,
								className: EditInPlace_module_default.button,
								disabled: state === 2,
								iconOnly: true,
								Icon: CloseIcon
							})
						})]
					})]
				}),
				state === 2 ? /* @__PURE__ */ jsx(LoadingMessage, { children: savingLabel }) : children,
				savedLabel && state === 3 && /* @__PURE__ */ jsx(SuccessMessage, { children: savedLabel }),
				helpLabel && (state === 0 || state === 1) && /* @__PURE__ */ jsx(ValidityState, { children: (validity) => (validity === void 0 || validity.valid) && !serverInvalid && /* @__PURE__ */ jsx(HelpMessage, { children: helpLabel }) })
			]
		})
	});
});
//#endregion
export { EditInPlace };

//# sourceMappingURL=EditInPlace.js.map